import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CircularProgress from 'apollo-react/components/CircularProgress';
import NewspaperFold from 'apollo-react-icons/NewspaperFold';

import Typography from '../StyledTypography';
import styles from './ClarificationWaves.module.css';
import tabStyles from './ClarificationWavesTabs.module.css';
import ClarificationWavesHeader from './ClarificationWavesHeader';
import SubformNavigation from '../SubformNavigation/SubformNavigation';
import RequestBadges from '../RequestBadges/RequestBadges';
import FormCrafterListView from '../FormCrafterFormRenderer/FormCrafterListView';

import useSubformNavigation from '../../../hooks/ui/navigation/useSubformNavigation';
import useClarificationWavesApi from '../../../hooks/api/requests/useClarificationWavesApi';

const RequestClarificationWaves = ({ requestId, requestProgressId }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [selectedWave, setSelectedWave] = useState('');
  const [availableWavesForms, setAvailableWavesForms] = useState({});
  const [loadingWaves, setLoadingWaves] = useState(true);
  const [selectedWaveFormData, setSelectedWaveFormData] = useState(null);
  const [loadingWaveFormData, setLoadingWaveFormData] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const availableForms = useMemo(() => {
    return availableWavesForms[selectedWave] || [];
  }, [availableWavesForms, selectedWave]);

  const activeForm = useMemo(() => {
    return selectedWaveFormData?.form || null;
  }, [selectedWaveFormData]);

  const formTabs =
    availableForms.map((form, index) => ({
      key: form.id || index,
      label: form.name || `${t('Form')} ${index + 1}`,
    })) || [];

  const { getClarificationWavesNumbersForms, getClarificationWaveFormData } =
    useClarificationWavesApi();

  const fetchWavesNumbersForms = useCallback(async () => {
    if (!requestId) {
      setLoadingWaves(false);
      setAvailableWavesForms({});
      return;
    }

    setLoadingWaves(true);
    try {
      const response = await getClarificationWavesNumbersForms(requestId);
      if (response.data.success && response.data.waves) {
        setAvailableWavesForms(response.data.waves);

        const urlParams = new URLSearchParams(location.search);
        const waveFromUrl = urlParams.get('wave');
        const wavesNumbers = Object.keys(response.data.waves);

        if (waveFromUrl && wavesNumbers.find((w) => w === waveFromUrl)) {
          setSelectedWave(waveFromUrl);
        } else if (wavesNumbers.length > 0) {
          setSelectedWave(wavesNumbers[wavesNumbers.length - 1]);
        }
      }
    } catch (error) {
      console.error('Error fetching clarification waves:', error);
    } finally {
      setLoadingWaves(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, getClarificationWavesNumbersForms]);

  useEffect(() => {
    fetchWavesNumbersForms();
  }, [requestId, fetchWavesNumbersForms]);

  const fetchWaveFormData = useCallback(async () => {
    if (!requestId || !selectedWave || !availableForms[activeTab]) return;

    const activeFormId = availableForms[activeTab].id;

    setLoadingWaveFormData(true);
    try {
      const response = await getClarificationWaveFormData(
        requestId,
        selectedWave,
        activeFormId
      );
      if (response.data.success && response.data.data) {
        setSelectedWaveFormData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching wave form data:', error);
    } finally {
      setLoadingWaveFormData(false);
    }
  }, [
    requestId,
    selectedWave,
    availableForms,
    activeTab,
    getClarificationWaveFormData,
  ]);

  useEffect(() => {
    fetchWaveFormData();
  }, [requestId, selectedWave, availableForms, activeTab, fetchWaveFormData]);

  const handleWaveChange = useCallback((event) => {
    const waveNumber = event.target.value;
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('wave', waveNumber);
    currentUrl.searchParams.set('tab', 'clarifications');
    window.open(currentUrl.toString(), '_blank');
  }, []);

  const handleTabChange = useCallback((value) => {
    setActiveTab(value);
  }, []);

  const {
    activeIndex: activeSubformIndex,
    navigate: navigateSubform,
    reset: resetSubformNavigation,
    hasMultipleItems: hasMultipleSubforms,
  } = useSubformNavigation(activeForm?.subforms || []);

  // Reset subform navigation when form tab changes
  useEffect(() => {
    resetSubformNavigation();
  }, [activeTab, resetSubformNavigation]);

  const waveOptions = useMemo(() => {
    return Object.keys(availableWavesForms).map((waveNumber) => ({
      value: waveNumber,
      label: t('clarificationWaves.wave', { number: waveNumber }),
    }));
  }, [availableWavesForms, t]);

  if (loadingWaves) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (!availableWavesForms || Object.keys(availableWavesForms).length === 0) {
    return (
      <div className={styles.emptyState}>
        <Typography variant="bodyDefault" color="textSecondary">
          {t('clarificationWaves.noWaves')}
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ClarificationWavesHeader
        selectedWave={selectedWave}
        waveOptions={waveOptions}
        onWaveChange={handleWaveChange}
        loadingWaveFormData={loadingWaveFormData}
        selectedWaveFormData={{
          sentBackBy: selectedWaveFormData?.wave?.sentBackBy,
          createdAt: selectedWaveFormData?.wave?.createdAt,
        }}
      />

      <div className={styles.contentContainer}>
        <div className={tabStyles.tabsContainer}>
          {formTabs.map((tab, index) => (
            <div
              key={tab.key}
              className={`${tabStyles.tabCard} ${activeTab === index ? tabStyles.active : ''}`}
              onClick={() => handleTabChange(index)}
            >
              <div className={tabStyles.tabIcon}>
                <NewspaperFold />
              </div>
              <div className={tabStyles.tabLabel}>{tab.label}</div>
            </div>
          ))}
        </div>

        {loadingWaveFormData ? (
          <div className={`${styles.loadingContainer} ${styles.fullWidth}`}>
            <CircularProgress />
          </div>
        ) : (
          activeForm &&
          selectedWaveFormData?.filledForm && (
            <div
              key={`form-content-${activeForm.id}`}
              className={styles.fullWidth}
            >
              <SubformNavigation
                items={activeForm?.subforms || []}
                activeIndex={activeSubformIndex}
                onNavigate={navigateSubform}
                waveSubformIds={selectedWaveFormData?.waveSubformIds}
                headerSlot={
                  <RequestBadges requestProgressId={requestProgressId} />
                }
              >
                <FormCrafterListView
                  key={`form-${activeForm.id}-${activeTab}-${activeSubformIndex}`}
                  subforms={activeForm.subforms}
                  activeSubformIndex={
                    hasMultipleSubforms ? activeSubformIndex : undefined
                  }
                  formId={activeForm.id}
                  filledForm={{
                    value: selectedWaveFormData?.filledForm
                      ? selectedWaveFormData?.filledForm
                      : null,
                  }}
                  fileMetadata={
                    selectedWaveFormData?.filledForm?.fileMetadata
                      ? selectedWaveFormData.filledForm.fileMetadata
                      : []
                  }
                  allowDeletedFiles={true}
                  enableAddComment={false}
                  requestId={requestId}
                />
              </SubformNavigation>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default RequestClarificationWaves;
